import {
  getPersistedDeviceMediaCapturePermission,
  persistDeviceMediaCapturePermission,
} from './config/devicePermissionStorage';

const PATCH_FLAG = '__jhGetUserMediaPersisted';
const PATCH_INSTALL_KEY = '__jhGetUserMediaPersistenceInstalled';
const PERMISSION_LISTENERS_KEY = '__jhMediaPermissionListenersAttached';

/**
 * Mirrors camera/mic permission outcomes into localStorage, sessionStorage, and cookie
 * (see devicePermissionStorage). Covers Matrix calls and upload capture, anything that uses
 * navigator.mediaDevices.getUserMedia after this module loads.
 *
 * The system permission sheet (e.g. iOS Safari "Would Like to Access the Microphone and
 * Camera") is enforced by the browser; persisted storage cannot prevent or replay that
 * prompt. It only records a last known observed outcome so heuristics can stay aligned with
 * what the user last chose — not authoritative truth (incognito, cleared site data, OS privacy
 * reset, Safari tab isolation, etc.).
 */

/**
 * Best-effort alignment with the Permissions API when camera/microphone descriptors are
 * supported. Many WebKit versions still reject or mis-report
 * `navigator.permissions.query({ name: 'camera' | 'microphone' })`; failures are ignored
 * and other code paths keep working.
 *
 * When both facets are queryable, avoids persisting combined "granted" if either is still prompt.
 */
export async function syncDeviceMediaCaptureFromPermissionsApi() {
  if (typeof navigator === 'undefined' || !navigator.permissions?.query) return;

  /** @type {PermissionState[]} */
  const states = [];
  for (const name of ['camera', 'microphone']) {
    try {
      const status = await navigator.permissions.query({ name });
      if (status?.state === 'granted' || status?.state === 'denied' || status?.state === 'prompt') {
        states.push(status.state);
      }
    } catch {
      // Descriptor unsupported (common on some WebKit versions).
    }
  }

  if (states.length === 0) return;

  if (states.some((s) => s === 'denied')) {
    persistDeviceMediaCapturePermission('denied');
    return;
  }

  if (states.every((s) => s === 'granted')) {
    persistDeviceMediaCapturePermission('granted');
  }
}

function attachPermissionStatusListener(status) {
  const handler = () => {
    void syncDeviceMediaCaptureFromPermissionsApi();
  };

  if (status?.addEventListener) {
    status.addEventListener('change', handler);
  } else if (status && 'onchange' in status) {
    status.onchange = handler;
  }
}

function attachDeviceMediaCapturePermissionChangeSync() {
  if (typeof navigator === 'undefined') return;
  if (!navigator.permissions?.query) return;

  if (typeof window !== 'undefined') {
    if (window[PERMISSION_LISTENERS_KEY]) return;
    window[PERMISSION_LISTENERS_KEY] = true;
  }

  (async () => {
    for (const name of ['camera', 'microphone']) {
      try {
        const status = await navigator.permissions.query({ name });
        attachPermissionStatusListener(status);
      } catch {
        // ignore unsupported descriptors
      }
    }
  })();
}

/** @param {MediaStreamConstraints | undefined} constraints */
function requestedAudioAndVideo(constraints) {
  if (!constraints || typeof constraints !== 'object') {
    return { audio: false, video: false };
  }
  return {
    audio: !!constraints.audio,
    video: !!constraints.video,
  };
}

export function installMediaDevicesGetUserMediaPersistence() {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) return;
  if (typeof window !== 'undefined' && window[PATCH_INSTALL_KEY]) return;

  const mediaDevices = navigator.mediaDevices;
  if (mediaDevices[PATCH_FLAG]) {
    if (typeof window !== 'undefined') {
      window[PATCH_INSTALL_KEY] = true;
    }
    return;
  }

  const originalGetUserMedia = mediaDevices.getUserMedia.bind(mediaDevices);
  const patchedDevices = mediaDevices;
  patchedDevices[PATCH_FLAG] = true;
  if (typeof window !== 'undefined') {
    window[PATCH_INSTALL_KEY] = true;
  }

  mediaDevices.getUserMedia = function getUserMediaWithPersistence(constraints) {
    return originalGetUserMedia(constraints).then(
      (stream) => {
        const { audio: requestedAudio, video: requestedVideo } = requestedAudioAndVideo(constraints);
        if (requestedAudio || requestedVideo) {
          persistDeviceMediaCapturePermission('granted');
        } else {
          void syncDeviceMediaCaptureFromPermissionsApi();
        }
        return stream;
      },
      (err) => {
        const name = err && err.name;
        // NotAllowedError is not always user denial (insecure origin, transient activation,
        // iframe policy, etc.); reconcile from Permissions API when available instead of
        // persisting a blanket "denied".
        if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
          void syncDeviceMediaCaptureFromPermissionsApi();
        }
        return Promise.reject(err);
      }
    );
  };
}

installMediaDevicesGetUserMediaPersistence();
void syncDeviceMediaCaptureFromPermissionsApi();
attachDeviceMediaCapturePermissionChangeSync();

export { getPersistedDeviceMediaCapturePermission };
